import {
  isCancelledError,
  QueryCache,
  QueryClient,
  QueryClientConfig,
  QueryKey,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';

import { useOnline } from '@mint-lib/hooks';
import { enqueueSnackbar } from '@mint-lib/notifications-api';

export * from '@tanstack/react-query';

export const createQueryClient = (options: QueryClientConfig = {}) =>
  new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        // for failed background update show snackbar
        const entityName = query?.options?.meta?.entityName;
        const isPresentInCache = query.state.data !== undefined;
        if (entityName && isPresentInCache) {
          enqueueSnackbar({
            title: 'Server error',
            subTitle: `Failed to synchronize ${entityName}`,
            variant: 'error',
          });
        }
      },
    }),
    defaultOptions: {
      queries: {
        throwOnError(error, query) {
          if (isCancelledError(error) || ('silent' in error && error.silent)) {
            return false;
          }
          if (!query.options?.meta?.crucial) {
            return false;
          }
          const notInCache = query.state.data === undefined;

          return Boolean(notInCache && navigator.onLine);
        },
      },
    },
    ...options,
  });

/**
 * @deprecated use native `useQuery` instead.
 */
export const useCrucialQuery = <
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
) => {
  // we want to handle case with no internet connection differently
  const isOnline = useOnline();

  return useQuery<TQueryFnData, TError, TData, TQueryKey>({
    throwOnError(error, query) {
      if (
        isCancelledError(error) ||
        (typeof error === 'object' &&
          error &&
          'silent' in error &&
          error.silent)
      ) {
        return false;
      }
      const notInCache = query.state.data === undefined;

      return Boolean(notInCache && isOnline);
    },
    ...options,
  });
};
